import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { LOTTERY_TYPES } from "../../config/lotteryConfig";

export default function LotteryLocalNav({ lotteryKey, placement = "mainHeader", activeKey }) {
  const location = useLocation();
  const config = LOTTERY_TYPES[lotteryKey];
  const [latestRounds, setLatestRounds] = useState({});

  // 全くじの最新回号を取得（横移動で最新ページに飛ぶため）
  useEffect(() => {
    const fetchAll = async () => {
      const keys = ["lt6", "lt7", "mlt", "nm3", "nm4", "bg5", "qcc"];
      const results = await Promise.all(
        keys.map(async (key) => {
          const { data } = await supabase
            .schema('chatorahack')
            .rpc('get_lottery_numlot_index_summary', {
              lottery_key: key,
              limit_count: 1,
              offset_count: 0,
              sort_column: 'round',
              sort_order: 'DESC',
            });
          return { key, round: data?.[0]?.round ?? null };
        })
      );
      const map = {};
      results.forEach(({ key, round }) => { map[key] = round; });
      setLatestRounds(map);
    };
    fetchAll();
  }, []);

  // 現在のページ種別を判定
  const getPageType = () => {
    const path = location.pathname;
    if (path.includes('/stats')) return 'stats';
    if (path.includes('/detail/')) return 'detail';
    return 'index';
  };
  const pageType = getPageType();

  // くじ横断ナビのリンク先を現在のページ種別に合わせる
  const getNavTo = (navKey) => {
    const c = LOTTERY_TYPES[navKey];
    if (pageType === 'stats') return c.statsPath || c.path;
    if (pageType === 'detail') {
      const round = latestRounds[navKey];
      return round ? `${c.path}detail/${round}` : c.path;
    }
    return c.path;
  };

  const latestRound = latestRounds[lotteryKey];

  const pageNavItems = [
    {
      to: config.path,
      label: "一覧",
      isActive: pageType === 'index',
      disabled: false,
    },
    {
      to: latestRound ? `${config.path}detail/${latestRound}` : null,
      label: "最新",
      isActive: pageType === 'detail' && location.pathname === `${config.path}detail/${latestRound}`,
      disabled: !latestRound,
    },
    ...(config.statsPath ? [{
      to: config.statsPath,
      label: "統計",
      isActive: pageType === 'stats',
      disabled: false,
    }] : []),
  ];

  const isHeader = placement === "mainHeader";

  const LotteryRow = (keys, rounded) => (
    <div className={`flex bg-base-50 border border-primary-500 overflow-hidden ${rounded}`}>
      {keys.map((navKey) => {
        const c = LOTTERY_TYPES[navKey];
        const isActive = activeKey
          ? navKey === activeKey
          : location.pathname.startsWith(c.path.slice(0, -1));
        return (
          <Link key={navKey} to={getNavTo(navKey)}
            className={`flex-1 text-center text-xs py-1.5 px-1 border-r last:border-r-0 border-primary-500 transition-colors
              ${isActive ? "bg-primary-500 text-base-50 font-bold" : "text-primary-500 hover:bg-primary-500/10"}`}
          >
            {c.shortLabel}
          </Link>
        );
      })}
    </div>
  );

  const PageNav = (rounded) => (
    <div className={`flex bg-base-50 border border-primary-500 overflow-hidden ${rounded}`}>
      {pageNavItems.map((item, i) => (
        item.disabled ? (
          <span key={i}
            className="flex-1 text-center text-sm font-bold py-2 border-r last:border-r-0 border-primary-500 text-primary-500/30"
          >
            {item.label}
          </span>
        ) : (
          <Link key={i} to={item.to}
            className={`flex-1 text-center text-sm font-bold py-2 border-r last:border-r-0 border-primary-500 transition-colors
              ${item.isActive ? "bg-primary-500 text-base-50" : "text-primary-500 hover:bg-primary-500/10"}`}
          >
            {item.label}
          </Link>
        )
      ))}
    </div>
  );

  return (
    <div className="mb-4">
      {isHeader ? (
        <div className="container max-w-[768px] mx-auto">
          {LotteryRow(["lt6", "lt7", "mlt"], "rounded-t-lg border-b-0")}
          {LotteryRow(["nm3", "nm4", "bg5", "qcc"], "")}
          {PageNav("rounded-b-lg mt-[2px]")}
        </div>
      ) : (
        <div className="container max-w-[768px] mx-auto mt-16">
          {PageNav("rounded-t-lg mb-[2px]")}
          {LotteryRow(["lt6", "lt7", "mlt"], "border-b-0")}
          {LotteryRow(["nm3", "nm4", "bg5", "qcc"], "rounded-b-lg")}
        </div>
      )}
    </div>
  );
}
